import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  AppBar,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import AMenu from "components/AMenu";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import { StyledContainer } from "components/StyledContainer";
import Transition from "components/Transition";
import { jobIdParser } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { CommentNotificationType } from "operations/schema/schema";
import { FC, useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { getNotifications, markAsRead, setNotificationsOpen } from "store/slices/comments.store";
import { CommentNotification } from "./CommentNotification";

type NotificationsDialogProps = {};

export const NotificationsDialog: FC<NotificationsDialogProps> = (props) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { notificationsOpen, notifications } = useAppSelector((s) => s.comments);
  const { userVar } = useAppSelector((s) => s.user);
  const [drawerMenuAnchorEl, setDrawerMenuAnchorEl] = useState<null | HTMLElement>(null);

  const loading = false;

  const handleClose = useCallback(() => {
    dispatch(setNotificationsOpen({ open: false }));
  }, [dispatch]);

  const onMarkAllRead = useCallback(() => {
    dispatch(markAsRead({ notificationIds: notifications.map((x) => x.notificationId) }))
      .then(unwrapResult)
      .then(() => dispatch(getNotifications({ force: true })));
  }, [dispatch, notifications]);

  const onMarkAsRead = useCallback(
    (notificationId: number) => {
      dispatch(markAsRead({ notificationIds: [notificationId] }))
        .then(unwrapResult)
        .then(() => dispatch(getNotifications({ force: true })));
    },
    [dispatch],
  );

  const onViewDetails = useCallback(
    (notification: CommentNotificationType) => {
      dispatch(setNotificationsOpen({ open: false }));
      if (notification.assignedTechnicianId === userVar?.technicianId) {
        navigate(`/jobs/${notification.jobId}`, {
          state: {
            openComment: true,
          },
        });
      } else if (
        technicianSettings.advancedPlannerAccess ||
        (technicianSettings.simplePlannerAccess && !notification.assignedTechnicianId)
      ) {
        var evaticId = jobIdParser(notification.jobId!);
        navigate(`/planner/${evaticId.ProjectTaskId}-${evaticId.ProjectNo}-${evaticId.TaskNo}-0`, {
          state: {
            openComment: true,
          },
        });
      }
    },
    [dispatch, navigate, userVar?.technicianId],
  );

  const sortedNotifications = useMemo(() => {
    let newNotifs = notifications ? [...notifications] : [];
    newNotifs = newNotifs
      ? newNotifs.sort((a, b) => {
          return (
            new Date(a.createdDate ?? new Date()).getTime() -
            new Date(b.createdDate ?? new Date()).getTime()
          );
        })
      : [];
    return newNotifs;
  }, [notifications]);

  return (
    <Dialog
      fullScreen
      open={notificationsOpen}
      onClose={handleClose}
      TransitionComponent={Transition}
      data-testid="CommentsDialog"
    >
      <BackdropPrimaryMain open={loading}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="CommentsDialog-CloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography className="AppBar-label" paddingRight="36px">
            <FormattedMessage id="notifications.title" />
          </Typography>

          <IconButton
            edge="end"
            color="inherit"
            onClick={(e) => setDrawerMenuAnchorEl(e.currentTarget)}
            aria-label={intl.formatMessage({ id: "menu" })}
            size="large"
            data-testid="CommentsDialog-Menu"
          >
            <MoreVertIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <StyledContainer>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            {sortedNotifications?.map((x) => (
              <CommentNotification
                key={x.notificationId}
                commentNotice={x}
                markAsRead={onMarkAsRead}
                onViewDetails={onViewDetails}
              />
            ))}
          </Grid>
        </Grid>
      </StyledContainer>
      <AMenu
        open={Boolean(drawerMenuAnchorEl)}
        anchorEl={drawerMenuAnchorEl}
        onClose={() => setDrawerMenuAnchorEl(null)}
        style={{ zIndex: 1400 }}
      >
        <MenuItem
          onClick={() => {
            onMarkAllRead();
            setDrawerMenuAnchorEl(null);
          }}
        >
          <FormattedMessage id="notifications.markAllAsRead" />
        </MenuItem>
      </AMenu>
    </Dialog>
  );
};
